import React, { useMemo } from 'react';
import { defaults, Pie } from 'react-chartjs-2';
import _replace from 'lodash/replace';
import * as PropTypes from 'prop-types';

import { generateRandomKey, scrollTo } from '../../utils';

import classes from './index.module.css';

// Global chart configs
defaults.global.hover.animationDuration = 100;

const defaultTooltipBodyTemplate = '<span>{name}:</span> <strong>{percent}%</strong>';

const chartGap = 20;

let segment;
let prevSegment;
let selectedIndex;
let prevSelectedIndex;

const PieChartGraph = (props) => {
  const {
    chartData,
    diameter,
    tooltipBodyTemplate,
    segmentsTooltipStyles,
    chartIsClickable,
    graphHeader,
    graphFooter,
    desc,
  } = props;

  const tooltipId = useMemo(() => generateRandomKey(), []); //  Instead of default prop or const, we used useMemo() because while re render it generates new key so, that cause this bug: FRON-10510

  const _onHover = (evt, elements) => {
    if (elements && elements.length) {
      segment = elements[0];
      selectedIndex = segment['_index'];

      if (prevSelectedIndex !== selectedIndex) {
        segment._model.outerRadius += 10;
        if (prevSegment) {
          prevSegment._model.outerRadius -= 10;
        }
      }

      prevSegment = segment;
      prevSelectedIndex = prevSegment['_index'];
    } else {
      if (prevSegment) {
        prevSegment._model.outerRadius -= 10;
      }
      prevSegment = null;
      prevSelectedIndex = null;
    }
  };

  const _onClick = (evt, elements) => {
    if (elements && elements.length) {
      if (chartIsClickable) {
        const clickSegment = elements[0];
        const clickSegmentIndex = clickSegment['_index'];
        const item = chartData[clickSegmentIndex] || {};
        const { link } = item;
        if (link) {
          scrollTo(link, 550);
        }
      }
    }
  };

  const _getOptions = () => {
    return {
      maintainAspectRatio: false,
      onHover: _onHover,
      onClick: _onClick,
      responsive: false,
      layout: {
        padding: 10,
      },
      title: {
        display: false,
      },
      legend: {
        display: false,
      },
      tooltips: {
        displayColors: false,
        callbacks: {
          label: function (tooltipItem, data) {
            return data.labels[tooltipItem.index] || {};
          },
        },

        // Disable the on-canvas tooltip
        enabled: false,

        custom: function (tooltipModel) {
          // Tooltip Element
          var tooltipEl = document.getElementById(`pie-chart-tooltip-${tooltipId}`);

          // Create element on first render
          if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = `pie-chart-tooltip-${tooltipId}`;
            tooltipEl.classList.add('pie-chart-tooltip-top');
            tooltipEl.classList.add(classes.tooltipTop);
            tooltipEl.classList.add('position-absolute');
            tooltipEl.classList.add('z-px-10');
            tooltipEl.classList.add('z-py-8');
            tooltipEl.classList.add('z-br-4');
            document.body.appendChild(tooltipEl);
          }

          // Hide if no tooltip
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = 0;
            return;
          }

          const position = this._chart.canvas.getBoundingClientRect();

          // Set default tooltip styles
          tooltipEl.style.opacity = 1;
          tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
          tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';

          // Set custom tooltip styles
          Object.keys(segmentsTooltipStyles).forEach((key) => {
            tooltipEl.style[key] = segmentsTooltipStyles[key];
          });

          // Set tooltip content
          tooltipEl.innerHTML = _getTooltipContent(tooltipModel);
        },
      },
    };
  };

  const _getTooltipContent = (tooltipModel) => {
    const bodyLines = tooltipModel.body.map((bodyItem) => bodyItem.lines)[0];
    return bodyLines[0];
  };

  const _getLabels = () => {
    const result = [];
    chartData.forEach((item) => {
      let temp = tooltipBodyTemplate;
      Object.keys(item).forEach((key) => {
        temp = _replace(temp, `\{${key}\}`, item[key]);
      });
      result.push(temp);
    });

    return result;
  };

  const _getDatasets = () => {
    const datasetItems = getDatasetItems();
    const segmentColors = chartData.map((item) => item.color);

    return [
      {
        data: desc ? datasetItems : [...datasetItems].reverse(),
        backgroundColor: desc ? segmentColors : [...segmentColors].reverse(),
        hoverBackgroundColor: desc ? segmentColors : [...segmentColors].reverse(),
        borderWidth: 2,
        hoverBorderWidth: 2,
        borderColor: '#fff',
        hoverBorderColor: '#fff',
      },
    ];
  };

  const getDatasetItems = () => {
    const result = [];
    chartData.forEach((item) => {
      let { percent } = item;
      if (percent) {
        if (typeof percent === 'string') {
          percent = Number(percent);
        }
        result.push(percent);
      }
    });

    return result;
  };

  const options = _getOptions();
  const datasets = _getDatasets();
  const labels = _getLabels();

  const data = {
    datasets,
    labels: desc ? labels : [...labels].reverse(),
  };

  return (
    <div className="pie-chart-graph-container position-relative z-mr-20">
      {graphHeader && graphHeader}
      <Pie width={diameter + chartGap} height={diameter + chartGap} options={options} data={data} />
      {graphFooter && graphFooter}
    </div>
  );
};

const propTypes = {
  chartData: PropTypes.arrayOf(PropTypes.any),
  diameter: PropTypes.number,
  tooltipBodyTemplate: PropTypes.string,
  segmentsTooltipStyles: PropTypes.objectOf(PropTypes.any),
  chartIsClickable: PropTypes.bool,
  graphHeader: PropTypes.any,
  graphFooter: PropTypes.any,
  desc: PropTypes.bool,
};

const defaultProps = {
  chartData: [],
  diameter: 240,
  tooltipBodyTemplate: defaultTooltipBodyTemplate,
  segmentsTooltipStyles: {},
  chartIsClickable: false,
  graphHeader: '',
  graphFooter: '',
  desc: true,
};

PieChartGraph.propTypes = propTypes;
PieChartGraph.defaultProps = defaultProps;

export default PieChartGraph;
